/**
 * FO-150 (batch-30, stream opleiding-klantmail) — klant informeren bij een
 * cursus-conflict.
 *
 * Gedeelde Zod-schema's + DTO's voor:
 *   GET  /opleiding-sessies/:id/klant-conflicten   (aggregatie per klant)
 *   POST /opleiding-sessies/:id/klant-mail/voorbeeld (preview = verzending)
 *   POST /opleiding-sessies/:id/klant-mail          (concept/verzenden/overslaan)
 *
 * De API parse't deze schema's server-side én het dialoog valideert er
 * client-side mee (CLAUDE.md §Validatie & foutmeldingen — één bron).
 */
import { z } from 'zod'
import { idParam } from './id-validators'
import type { WerkbonVerstuurContactDTO } from './werkbon-verzending'

// ─── Status ──────────────────────────────────────────────────────────────────

export const OPLEIDING_SESSIE_KLANT_MAIL_STATUS = [
  'CONCEPT',
  'VERZONDEN',
  'VEROUDERD',
  'OVERGESLAGEN',
] as const
export type OpleidingSessieKlantMailStatus =
  (typeof OPLEIDING_SESSIE_KLANT_MAIL_STATUS)[number]

export const OPLEIDING_KLANT_MAIL_STATUS_LABELS: Record<
  OpleidingSessieKlantMailStatus,
  string
> = {
  CONCEPT: 'Concept bewaard',
  VERZONDEN: 'Klant geïnformeerd',
  VEROUDERD: 'Verouderd (dagen gewijzigd)',
  OVERGESLAGEN: 'Overgeslagen',
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** Eén gecommuniceerde cursusdag (yyyy-MM-dd + optionele HH:mm-tijden). */
export interface OpleidingKlantMailDagDTO {
  datum: string
  startTijd: string | null
  eindTijd: string | null
}

export interface OpleidingSessieKlantMailDTO {
  id: string
  sessieId: string
  clientId: string
  status: OpleidingSessieKlantMailStatus
  /** Ruwe (bewerkbare) tekst — placeholders nog niet ingevuld. */
  onderwerp: string
  body: string
  metVervangingsvraag: boolean
  /** FO-302 — gebundelde medewerkers (was `deelnameIds` op het b30-model). */
  employeeIds: string[]
  /** Snapshot van de gecommuniceerde dagen op moment van opstellen. */
  dagen: OpleidingKlantMailDagDTO[]
  ontvangers: string[]
  cc: string[]
  outgoingEmailId: string | null
  verzondenOp: string | null
  createdAt: string
}

export interface OpleidingKlantConflictDeelnemerDTO {
  deelnameId: string
  employeeId: string
  naam: string
}

export interface OpleidingKlantConflictWerkbonDTO {
  werkbonId: string
  werkbonNumber: string
}

/**
 * Eén kaart per geraakte klant: de gebundelde deelnemers en alléén de
 * cursusdagen waarop die klant een planning-conflict heeft (FO-150 §6).
 */
export interface OpleidingKlantConflictDTO {
  clientId: string
  klantNaam: string
  deelnemers: OpleidingKlantConflictDeelnemerDTO[]
  dagen: OpleidingKlantMailDagDTO[]
  werkbonnen: OpleidingKlantConflictWerkbonDTO[]
  /** Laatste mail-actie voor deze (sessie, klant); null = nog geen actie. */
  mail: OpleidingSessieKlantMailDTO | null
}

export interface OpleidingKlantConflictenDTO {
  conflicten: OpleidingKlantConflictDTO[]
}

// ─── Voorbeeld (preview) ─────────────────────────────────────────────────────

/**
 * Body van POST /opleiding-sessies/:id/klant-mail/voorbeeld. Zonder
 * onderwerp/body rendert de server de standaard-template; mét = live preview
 * van de bewerkte tekst. `klantMailId` rendert een bewaard concept vanaf zijn
 * eigen dagen-snapshot (wat verstuurd zou worden), i.p.v. live conflict-data.
 */
export const OpleidingKlantMailVoorbeeldSchema = z.object({
  clientId: idParam('clientId'),
  klantMailId: idParam('klantMailId').optional(),
  onderwerp: z.string().trim().min(1).max(500).optional(),
  body: z.string().trim().min(1).max(20000).optional(),
  metVervangingsvraag: z.boolean().optional(),
})
export type OpleidingKlantMailVoorbeeldInput = z.infer<
  typeof OpleidingKlantMailVoorbeeldSchema
>

export interface OpleidingKlantMailVoorbeeldDTO {
  klantNaam: string
  /** Voorgestelde ontvangers uit de vaste-ontvanger-cascade (FO-112). */
  defaultTo: string[]
  defaultCc: string[]
  /** Contactpersonen mét e-mailadres, voor de ontvanger-picker. */
  contacten: WerkbonVerstuurContactDTO[]
  /** Leesbare context "Freek Twilt - hoofd TD" of `null` (lege-ontvanger-hint). */
  vasteOntvangerLabel: string | null
  /** E-mailadres van de ingelogde gebruiker (voor "Mijzelf in cc"). */
  eigenEmail: string | null
  /** Ruwe bewerkbare tekst (placeholders zichtbaar) voor de editor. */
  bewerkbaar: { onderwerp: string; body: string }
  /** Gerenderde preview via dezelfde pipeline als de verzending (FO-111). */
  voorbeeld: { onderwerp: string; bodyHtml: string }
  /** Effectieve stand van de vervangingsvraag (input > concept > default aan). */
  metVervangingsvraag: boolean
  /** De dagen + deelnemers die in de mail gecommuniceerd worden. */
  dagen: OpleidingKlantMailDagDTO[]
  deelnemers: OpleidingKlantConflictDeelnemerDTO[]
}

// ─── Concept bewaren / versturen / overslaan ─────────────────────────────────

const emailAdres = z.string().trim().email('Ongeldig e-mailadres')

const klantMailTekstVelden = {
  onderwerp: z.string().trim().min(1, 'Onderwerp is verplicht').max(500),
  body: z.string().trim().min(1, 'Bericht is verplicht').max(20000),
  metVervangingsvraag: z.boolean(),
  cc: z.array(emailAdres).max(20).default([]),
}

/**
 * Body van POST /opleiding-sessies/:id/klant-mail. Drie acties:
 *   - CONCEPT:   bewaren om later te versturen (ontvangers mogen nog leeg zijn);
 *   - VERZENDEN: direct versturen — minstens één geldige ontvanger verplicht,
 *                nooit een verborgen fallback (bewuste actie, FO-150 §6);
 *   - OVERSLAAN: bewust niet mailen (bv. al telefonisch geïnformeerd).
 * `klantMailId` richt de actie op een bestaand concept (bewerken/versturen).
 */
export const OpleidingKlantMailSchema = z.discriminatedUnion('actie', [
  z.object({
    actie: z.literal('CONCEPT'),
    clientId: idParam('clientId'),
    klantMailId: idParam('klantMailId').optional(),
    to: z.array(emailAdres).max(20).default([]),
    ...klantMailTekstVelden,
  }),
  z.object({
    actie: z.literal('VERZENDEN'),
    clientId: idParam('clientId'),
    klantMailId: idParam('klantMailId').optional(),
    to: z.array(emailAdres).min(1, 'Minstens één ontvanger is verplicht').max(20),
    maakOpvolgTaak: z.boolean().default(false),
    ...klantMailTekstVelden,
  }),
  z.object({
    actie: z.literal('OVERSLAAN'),
    clientId: idParam('clientId'),
  }),
])
export type OpleidingKlantMailInput = z.infer<typeof OpleidingKlantMailSchema>

/** Response van POST /opleiding-sessies/:id/klant-mail. */
export interface OpleidingKlantMailResultDTO {
  klantMail: OpleidingSessieKlantMailDTO
  /**
   * Alleen bij actie=VERZENDEN: uitkomst van de mail-queue. `QUEUED` = verstuurd
   * (wacht op de worker); `DROPPED_ALLOWLIST` = niet verstuurd (allowlist/opt-out)
   * — de rij blijft dan CONCEPT zodat opnieuw versturen mogelijk is.
   */
  verzendStatus?: 'QUEUED' | 'DROPPED_ALLOWLIST'
}
